/* eslint-disable jsx-a11y/anchor-is-valid */
import _ from 'underscore';
import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import {
  Button,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';

import { generateNameSuggestionSuffix } from '@bingads-webui-campaign/audience-campaign-shared';
import { ZoomControl } from './ad-previews/zoom-control';
import { DisplayAdsContext } from './context';
import { getAdConfig } from './utils';

export const useStyles = makeStyles({
  container: {
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    ...shorthands.padding(0, tokens.spacingHorizontalL),
  },
  saveButton: {
    marginLeft: tokens.spacingHorizontalL,
    whiteSpace: 'nowrap',
  },
});

export const DisplayTemplateSummaryBarControl = ({
  i18n,
  isSaveDisabled,
  zoomValue,
  onZoomSelect,
  dataService,
  saveInNewFolder,
  onSaveSuccess,
  setIsSaving,
  finalUrl,
  sourceType,
  folderId,
}) => {
  const classes = useStyles();
  const [summaryBar, setSummaryBar] = useState<HTMLElement| null>(null);
  const {
    state: {
      displayAds,
      templateConfigs,
      adImages,
    },
  } = useContext(DisplayAdsContext);
  const isSingleAd = useMemo(() => Object.keys(displayAds).length === 1, [displayAds]);
  const activity = useContext(ActivityContext) as any;

  const getFolder = useCallback(() => {
    if (saveInNewFolder && dataService.createFolder) {
      return dataService.createFolder({ folderName: generateNameSuggestionSuffix(i18n.getString(_TL_('Display template'))) });
    }
    return Promise.resolve(parseInt(folderId, 10) || 'ROOT');
  }, [dataService, saveInNewFolder, i18n, folderId]);

  const memoizedImageUpload = useMemo(
    () => _.memoize(asset => dataService.uploadImageByUrl(asset), asset => JSON.stringify(asset)),
    [dataService]
  );

  const memoizedGetImageById = useMemo(
    () => _.memoize(asset => dataService.getImageById(asset), asset => JSON.stringify(asset)),
    [dataService]
  );

  const uploadImageAsset = useCallback((asset) => {
    if (!asset || !asset.url) {
      return Promise.resolve(null);
    }
    if (asset.assetId) {
      return Promise.resolve({
        url: asset.url,
        id: asset.assetId,
      });
    }
    return memoizedImageUpload({
      imageUrl: asset.url,
      folderId: 'ROOT',
    }).then(imageId => memoizedGetImageById({ imageId }));
  }, [memoizedImageUpload, memoizedGetImageById]);

  const uploadImageAssets = useCallback(({ image, logo }) => Promise.all([
    uploadImageAsset(image),
    uploadImageAsset(logo),
  ]).then(([imageAsset, logoAsset]) => ({
    image: imageAsset,
    logo: logoAsset,
  })), [uploadImageAsset]);

  const save = useCallback(() => {
    setIsSaving(true);
    getFolder().then((folder) => {
      const uploadAssets = Object.keys(displayAds).map((size) => {
        const ad = displayAds[size];
        const templateConfig = templateConfigs[size];
        const adImage = adImages[size];
        const adConfig = getAdConfig(ad, templateConfig);

        return uploadImageAssets(ad).then(({ image, logo }) => {
          const creativeData = {
            ...adConfig,
            template: {
              ...adConfig.template,
              image: {
                ...adConfig.template.image,
                url: image.url,
              },
              ...(logo? { logo: { ...adConfig.template.logo, url: logo.url } } : {}),
            },
            image: image.url,
            url: adImage.url,
          };
          return dataService.addImageWithBaseIdToFolder({
            imageSrc: adImage.url,
            folderId: folder,
            metaData: JSON.stringify(creativeData),
            baseId: Number(image.id),
            sourceType: sourceType || 'DisplayTemplateGenerated',
          });
        });
      });

      return Promise.all(uploadAssets).then(() => {
        if (onSaveSuccess) {
          if (activity) {
            if (saveInNewFolder) {
              activity.signal(`[DisplayAdsEditor] saved display template to folder ${folder} with ${uploadAssets.length}`);
              if (finalUrl) {
                activity.signal(`[DisplayAdsEditor] saved display template with final url ${finalUrl}`);
              } else {
                activity.signal('[DisplayAdsEditor] saved display template without final url');
              }
            } else {
              activity.signal('[DisplayAdsEditor] edited display template image');
            }
          }
          onSaveSuccess();
        }
      }).catch(() => {
        // handle error
      }).finally(() => {
        setIsSaving(false);
      });
    });
  }, [displayAds, templateConfigs, adImages, dataService, uploadImageAssets, getFolder, onSaveSuccess, setIsSaving, finalUrl, activity, saveInNewFolder, sourceType]);

  const customizedContent = useMemo(() => (
    <div className={classes.container} >
      <ZoomControl
        zoomValue={zoomValue}
        onZoomSelect={onZoomSelect}
        i18n={i18n}
      />
      <Button
        appearance="primary"
        className={mergeClasses('save-template-button', classes.saveButton)}
        disabled={isSaveDisabled}
        onClick={save}
      >
        {isSingleAd? i18n.getString(_TL_('Save')): i18n.getString(_TL_('Save all sizes')) }
      </Button>
    </div>
  ), [
    classes.container,
    i18n,
    isSaveDisabled,
    save,
    onZoomSelect,
    zoomValue,
    classes.saveButton,
    isSingleAd,
  ]);

  useEffect(() => {
    const timer = setInterval(() => {
      try {
        const summaryBarCustomizeEle = document.getElementById('ad-studio-summary-bar-customize-container');
        if (summaryBarCustomizeEle) {
          setSummaryBar(summaryBarCustomizeEle);
          clearInterval(timer);
        }
      } catch (error) {
        clearInterval(timer);
      }
    }, 500);
    return () => clearInterval(timer);
  }, []);

  return (
    summaryBar ? createPortal(
      customizedContent,
      summaryBar
    ) : null
  );
};

DisplayTemplateSummaryBarControl.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isSaveDisabled: PropTypes.bool,
  zoomValue: PropTypes.string.isRequired,
  onZoomSelect: PropTypes.func.isRequired,
  dataService: PropTypes.shape({
    addImageWithBaseIdToFolder: PropTypes.func,
    createFolder: PropTypes.func,
    uploadImageByUrl: PropTypes.func,
    getImageById: PropTypes.func,
  }).isRequired,
  saveInNewFolder: PropTypes.bool,
  onSaveSuccess: PropTypes.func,
  setIsSaving: PropTypes.func.isRequired,
  finalUrl: PropTypes.string,
  sourceType: PropTypes.string,
  folderId: PropTypes.string,
};

DisplayTemplateSummaryBarControl.defaultProps = {
  isSaveDisabled: false,
  saveInNewFolder: false,
  onSaveSuccess: undefined,
  finalUrl: undefined,
  sourceType: undefined,
  folderId: undefined,
};
